import React, { useEffect, useState } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { API } from '../../../../api';
import { getLLAuthHeaders } from '../../utils/llAuthHeaders';
import { useCapabilityOrg } from '../../../../queries/useCapability';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { NAME_COLUMN, columnHeadingSx, helperTextSx, lockedChipSx, sectionTitleSx } from './orgSettingsStyles';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

// Roles — a locked list in Audience Attributes on Organization Settings, under
// Department. Each department's roles are added in Capabilities and show here
// read-only; members answer "Role in <department>" in their attributes,
// choosing from the roles that are set up (confirmed by their manager, and
// staying so while capabilities are added to them).
// Nothing here can be changed: it points to Capabilities.

interface SetupRole {
  id: string;
  teamId: string;
  name: string;
  capabilitiesConfirmed: boolean;
  levelsConfirmed: boolean;
  /** Set up, as the backend counts it (the role question offers these). Absent from a backend before it said so. */
  ready?: boolean;
}

const TeamRolesSection: React.FC<{ number: number; teamValues: Array<{ id: string; name: string }> }> = ({
  number,
  teamValues
}) => {
  const navigate = useNavigate();
  // A platform admin reads the org they have open, not their own.
  const org = useCapabilityOrg();
  const [roles, setRoles] = useState<SetupRole[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setRoles(null);
    setFailed(false);
    API.getCapabilitySetup({ ...getLLAuthHeaders(), orgId: org })
      .then((res: any) => {
        if (!alive) return;
        const body = res?.data?.data ?? res?.data;
        setRoles(Array.isArray(body?.roles) ? body.roles : []);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [org]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }} data-testid="team-roles-section">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          // A phone: Open Capabilities drops under the title rather than squeeze it.
          [MOBILE_SX]: { flexWrap: 'wrap', rowGap: '10px' }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Typography sx={sectionTitleSx}>{number}. Roles</Typography>
            <Tooltip title="Roles are added in Capabilities and can't be changed here">
              <LockOutlinedIcon sx={{ fontSize: 16, color: '#868686' }} />
            </Tooltip>
          </Box>
          <Typography sx={helperTextSx}>
            Learners will also be enrolled and capabilities will be assessed by role so please add here now.
          </Typography>
        </Box>
        <DosenButton
          sizeVariant="sm"
          tone="basic"
          sx={{ flexShrink: 0 }}
          onClick={() => navigate('/app/capability-setup')}
        >
          Open Capabilities
        </DosenButton>
      </Box>
      {failed ? (
        <Typography sx={helperTextSx}>The roles could not be loaded. Open Capabilities to see them.</Typography>
      ) : roles === null ? (
        <Typography sx={helperTextSx}>Loading roles...</Typography>
      ) : teamValues.length === 0 ? (
        <Typography sx={helperTextSx}>
          Roles show here once your departments are saved and given roles in Capabilities.
        </Typography>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `${NAME_COLUMN}px minmax(0, 1fr)`,
              gap: '16px',
              pb: '8px',
              // A phone: each department heads its own roles, so no column heads.
              [MOBILE_SX]: { display: 'none' }
            }}
          >
            <Typography sx={columnHeadingSx}>Department</Typography>
            <Typography sx={columnHeadingSx}>Roles</Typography>
          </Box>
          {teamValues.map((team) => {
            const own = roles.filter((r) => r.teamId === team.id);
            return (
              <Box
                key={team.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `${NAME_COLUMN}px minmax(0, 1fr)`,
                  gap: '16px',
                  alignItems: 'center',
                  py: '10px',
                  borderTop: '1px solid #2A2A2A',
                  [MOBILE_SX]: { gridTemplateColumns: 'minmax(0, 1fr)', gap: '8px', py: '12px' }
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontFamily: 'Inter', fontWeight: 600, color: '#FDFDFD', wordBreak: 'break-word' }}
                >
                  {team.name}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minHeight: 28, alignItems: 'center' }}>
                  {own.length ? (
                    own.map((r) => {
                      const ready =
                        typeof r.ready === 'boolean' ? r.ready : r.capabilitiesConfirmed && r.levelsConfirmed;
                      return (
                        <Chip
                          key={r.id}
                          icon={<LockOutlinedIcon />}
                          label={ready ? r.name : `${r.name} (being set up)`}
                          sx={{
                            ...lockedChipSx,
                            // A phone: a long role name wraps inside its chip rather than being cut.
                            [MOBILE_SX]: {
                              height: 'auto !important',
                              maxWidth: '100%',
                              py: '3px',
                              '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere' }
                            }
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography sx={{ ...helperTextSx, color: '#6E6E6E' }}>No roles yet</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default TeamRolesSection;
